"use client";

import { useRef } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ProofOfPower() {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const gallery = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_sprint_start.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_sprint_start.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_sprint_start.webp-wvc-sizes",
    alt: "Athlete at the starting blocks",
    caption: "EXPLOSIVE START / PHASE 01",
    height: "h-[55vh] md:h-[70vh]"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_heavy_lift.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_heavy_lift.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_heavy_lift.webp-wvc-sizes",
    alt: "Athlete performing a heavy deadlift",
    caption: "RAW POWER / PHASE 02",
    height: "h-[50vh] md:h-[55vh]"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_focus_gym.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_focus_gym.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_focus_gym.png-wvc-sizes",
    alt: "Focused athlete in the gym",
    caption: "MENTAL CLARITY / FOCUS",
    height: "h-[55vh] md:h-[70vh]"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_recovery_stretch.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_recovery_stretch.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_recovery_stretch.png-wvc-sizes",
    alt: "Athlete stretching after workout",
    caption: "RECOVERY / REBUILD",
    height: "h-[50vh] md:h-[55vh]"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_sprint_finish.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_sprint_finish.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_sprint_finish.webp-wvc-sizes",
    alt: "Athlete crossing the finish line",
    caption: "THE PEAK / PHASE 06",
    height: "h-[55vh] md:h-[70vh]"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_kettlebell_swing.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_kettlebell_swing.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_kettlebell_swing.png-wvc-sizes",
    alt: "Athlete performing kettlebell swings",
    caption: "KINETIC ENERGY / PHASE 01",
    height: "h-[50vh] md:h-[55vh]"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_dusk_training.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_dusk_training.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_dusk_training.png-wvc-sizes",
    alt: "Athlete training at dusk",
    caption: "ENDURANCE / NIGHT SESSION",
    height: "h-[55vh] md:h-[70vh]"
  }];
  const scrollBy = (amount: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: amount,
      behavior: "smooth"
    });
  };
  return <section data-section-id="3945" id="proof-of-power" className="relative w-full overflow-hidden py-[100px] md:py-[140px] bg-background text-foreground">
      {}
      <div ref={headerRef} className={`mx-auto max-w-4xl px-6 text-center transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <p className="font-mono text-xs uppercase tracking-[0.3em] mb-8 text-primary">
          — Proof of Power
        </p>
        <h2 className="font-default font-bold leading-[1.1] tracking-tight text-foreground" style={{
        fontSize: "clamp(2rem, 5vw, 3.5rem)"
      }}>
          Where human potential meets <em className="italic text-primary">force</em>.
        </h2>
      </div>

      {}
      <div className="relative mt-16 md:mt-24">
        <div ref={scrollRef} className="hidden md:flex overflow-x-auto overflow-y-hidden gap-8 px-[8vw] pb-6 scroll-smooth snap-x snap-mandatory" style={{
        scrollbarWidth: "none"
      }}>
          {gallery.map((item, i) => <figure data-index={i} key={i} className={`relative shrink-0 snap-start ${item.height} ${i === 0 ? "-ml-[4vw]" : ""}`} style={{
          width: "clamp(420px, 42vw, 720px)"
        }}>
              <div className="relative h-full w-full overflow-hidden rounded-lg shadow-lg">
                <img src={item.src} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                <div className="pointer-events-none absolute inset-0 mix-blend-overlay opacity-30" style={{
              backgroundImage: "radial-gradient(hsl(75 100% 55% / 0.4) 1px, transparent 1px)",
              backgroundSize: "3px 3px"
            }} />
              </div>
              <figcaption className="mt-5 font-mono text-[11px] tracking-[0.25em] text-muted-foreground">
                {item.caption}
              </figcaption>
            </figure>)}

          {}
          <div className="shrink-0 snap-start flex items-center justify-center pr-[6vw]" style={{
          minWidth: "320px"
        }}>
            <Link to="/JoinTheForcePage" className="group inline-flex items-center gap-3 font-default italic text-xl transition-colors text-foreground">
              <span className="underline-offset-8 group-hover:underline">View Full Results</span>
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1 text-primary" />
            </Link>
          </div>
        </div>

        {}
        <div className="hidden md:flex absolute bottom-10 right-[8vw] gap-3 z-10">
          <Button variant="outline" size="icon" aria-label="Scroll previous" onClick={() => scrollBy(-600)} className="h-12 w-12 rounded-lg border bg-transparent hover:bg-transparent transition-colors border-border text-foreground">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Button variant="outline" size="icon" aria-label="Scroll next" onClick={() => scrollBy(600)} className="h-12 w-12 rounded-lg border bg-transparent hover:bg-transparent transition-colors hover:text-primary hover:border-primary border-border text-foreground">
            <ArrowRight className="h-5 w-5" />
          </Button>
        </div>

        {}
        <div className="md:hidden flex flex-col gap-8 px-6">
          {gallery.map((item, i) => <figure data-index={i} key={i} className="relative">
              <div className="relative w-full h-[60vh] overflow-hidden rounded-lg shadow-lg">
                <img src={item.src} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                <div className="pointer-events-none absolute inset-0 mix-blend-overlay opacity-30" style={{
              backgroundImage: "radial-gradient(hsl(75 100% 55% / 0.4) 1px, transparent 1px)",
              backgroundSize: "3px 3px"
            }} />
              </div>
              <figcaption className="mt-4 font-mono text-[11px] tracking-[0.25em] text-muted-foreground">
                {item.caption}
              </figcaption>
            </figure>)}

          <div className="pt-4 pb-2 text-center">
            <Link to="/JoinTheForcePage" className="group inline-flex items-center gap-3 font-default italic text-lg text-foreground">
              <span className="underline underline-offset-8">View Full Results</span>
              <ArrowRight className="h-5 w-5 text-primary" />
            </Link>
          </div>
        </div>
      </div>
    </section>;
}